import type { MoneyV2 as MoneyV2Numeric } from '@shopify/web-pixels-extension'
import { isObject, uniqBy } from 'lodash-es'
import type { SetRequired } from 'type-fest'
import type { UnloadedCart } from '~/reducers/cart'
import type { API, Checkout, CountryCode, DiscountAllocation, LineItem, Money, Nullable } from '~/types'
import { useSelector } from '~/utils/hooks'
import type { IPricingPercentageValue } from '../types/shopify/.gql.types'
import { getCurrentCountry, getStoreCountry, useCurrentCountryCode } from './country'
import { hasDecimals, parseNumber } from './helpers'

type PricingValue = any

export function isMoneyV2(n: any): n is Money | API.MoneyV2 {
	return n ? !!(n as Money)?.amount : false
}

export type MoneyValue = Nullable<Money | API.MoneyV2 | number | string>

const currencies = uniqBy(
	typeof window !== 'undefined' ? window.localization?.available_countries.map(c => c.currency) : [],
	c => c.iso_code
)

/**
 * Formats price according to current locale and currency
 * @param price {@link MoneyValue}
 * @param asText if true format price with currency symbyl, if not, return price as float
 * @param decimals Number of decimals to format with. Only relevant if asText is true.
 * Will always display decimals on prices that would otherwise require rounding
 */
export function getPrice(price: MoneyValue, asText?: false, decimals?: number): number
export function getPrice(price: MoneyValue, asText?: true, decimals?: number): string
export function getPrice(price: MoneyValue, asText: boolean, decimals?: number): number | string
export function getPrice(price: MoneyValue, asText = false, decimals = 0): number | string {
	const value = isMoneyV2(price) ? parseNumber(price?.amount) : parseNumber(price)
	if (asText === true) {
		const fallback = getCurrentCountry().currency
		const code = isObject(price) ? price.currencyCode : fallback.iso_code
		const currency = currencies.find(c => c.iso_code === code)
		if (!currency) {
			console.warn('Currency not found', code)
			return `${fallback?.symbol}${value}`
		}
		try {
			const amount = decimals || hasDecimals(value) ? value.toFixed(decimals || 2) : value
			switch (getStoreCountry()) {
				case 'NO':
					return `${amount}${currency.symbol}`
				case 'SE':
					return `${amount} ${currency.symbol}`
				case 'EU':
					return currency.symbol.length > 1 ? `${amount} ${currency.symbol}` : `${currency.symbol}${amount}`
			}
		} catch (e) {
			console.warn(getPrice.name, e)
			return `${currency.symbol}${value}`
		}
	} else {
		return value
	}
}

export function toMoneyV2Numeric(price: Money): MoneyV2Numeric {
	return {
		amount: parseNumber(price.amount),
		currencyCode: price.currencyCode
	}
}

export function isPercentageDiscount(p: PricingValue): p is IPricingPercentageValue {
	return (p as IPricingPercentageValue).percentage !== undefined
}

export function getDiscountValue(discount: Pick<DiscountAllocation, 'discountedAmount'>) {
	return getPrice(discount.discountedAmount)
}

// export function getScriptDiscountValues(cart: Checkout, discount: Discount): number {
// 	const lineItems = cart.lines.filter(item => item.id === discount.variableValues?.id)
// 	if (lineItems.length > 0) {
// 		return lineItems.reduce((sum, item) => sum + getDiscountValue(item, discount), 0)
// 	} else {
// 		console.warn('Discount target ID not found', discount.variableValues?.id)
// 		return 0
// 	}
// }

// export function getScriptDiscountValues(cart: Checkout, discount: Discount): number {
// 	const lineItems = cart.lines.filter(item => item.id === discount.variableValues?.id)
// 	if (lineItems.length > 0) {
// 		return lineItems.reduce((sum, item) => sum + getDiscountValue(item, discount), 0)
// 	} else {
// 		console.warn('Discount target ID not found', discount.variableValues?.id)
// 		return 0
// 	}
// }

export function lineDiscount(item: Pick<LineItem, 'merchandise' | 'quantity' | 'discountAllocations'>): number {
	const { merchandise } = item
	const comparePrice = merchandise?.compareAtPrice
		? getPrice(merchandise.compareAtPrice) - getPrice(merchandise.price)
		: 0
	return getScriptDiscounts(item) + comparePrice * item.quantity
}

export function linePrice(item: LineItem): number {
	const { merchandise, quantity } = item
	if (!merchandise) return -1
	return getPrice(merchandise.compareAtPrice ?? merchandise.price) * quantity - lineDiscount(item)
}

export function lineComparePrice(item: LineItem): number {
	return linePrice(item) + lineDiscount(item)
}

export function getScriptDiscounts(item: Nullable<Pick<LineItem, 'discountAllocations'>>): number {
	if (!item?.discountAllocations) return 0
	return item.discountAllocations.reduce((a, discount) => a + getDiscountValue(discount), 0)
}

export function applyScriptDiscount(item: LineItem): number {
	const basePrice = getPrice(item.merchandise?.price)
	return item.discountAllocations.reduce((price, discount) => price - getDiscountValue(discount), basePrice)
}

export function cartCalcSubTotalDiscount(cart: Checkout) {
	return cart.lines.reduce((a, b) => a + lineDiscount(b), 0)
}

export function cartCalcSubTotal(cart: Checkout) {
	return cart.lines.reduce((a, b) => a + linePrice(b), 0)
}

export interface ShippingCostDetails {
	/** Whether the cart requires shipping */
	requiresShipping: boolean
	/** The cost of shipping */
	cost: number
	/** The amount saved in shipping costs based on the cart subtotal */
	saved: number
	/** The threshold for free shipping */
	threshold: number
	/** The minimum cost of shipping for the current setting */
	minCost: number
	/** The maximum cost of shipping for the current setting */
	maxCost: number
}

export function useShippingCost(cart: Checkout | UnloadedCart | null): ShippingCostDetails | null {
	const setting = useShippingSetting()
	const threshold = useFreeShippingThreshold()
	if (!cart || !cart.lines || !cart.cost) return null
	return getShippingCost(cart as SetRequired<typeof cart, 'lines' | 'cost'>, setting, threshold)
}

export function getShippingCost(
	cart: Pick<Checkout, 'lines' | 'cost'>,
	setting: API.ShippingSetting | null,
	threshold: number | null
): ShippingCostDetails | null {
	const requiresShipping = cart.lines.some(item => item.merchandise?.requiresShipping)
	if (!requiresShipping) return { requiresShipping, cost: 0, saved: 0, threshold: 0, maxCost: 0, minCost: 0 }

	const subtotal = getPrice(cart.cost.subtotalAmount)

	if (!setting) return null

	const rate = setting.conditions
		.filter(con =>
			con.criteria.every(c => {
				if (c.field !== 'TOTAL_PRICE') return false
				const amount = getPrice(c)
				switch (c.operator) {
					case 'LESS_THAN_OR_EQUAL_TO':
						return subtotal <= amount
					case 'GREATER_THAN_OR_EQUAL_TO':
						return subtotal >= amount
				}
			})
		)
		.sort((a, b) => getPrice(b.rate) - getPrice(a.rate))
		.pop()

	const rates = setting.conditions.map(c => getPrice(c.rate))
	const maxShippingCost = Math.max(0, ...rates)
	const minShippingCost = Math.min(...rates.filter(r => r > 0))
	const shippingCost = rate ? parseNumber(rate.rate.amount) : 0

	return {
		requiresShipping,
		cost: shippingCost,
		saved: minShippingCost - shippingCost,
		threshold: threshold ?? 0,
		minCost: minShippingCost,
		maxCost: maxShippingCost
	}
}

export function useFreeShippingThreshold(): number | null {
	const setting = useShippingSetting()
	return setting ? getFreeShippingThreshold(setting) : null
}

export function getFreeShippingThreshold(shippingSetting: API.ShippingSetting): number | null {
	const thresholds = shippingSetting?.conditions.flatMap(c => c.criteria).map(c => getPrice(c))
	return thresholds?.length ? Math.max(...thresholds) : null
}

export function useShippingSetting(): API.ShippingSetting | null {
	const shippingSettings = useSelector(s => s.shippingSettings)
	const country = useCurrentCountryCode()
	return getShippingSetting(shippingSettings, country as CountryCode)
}

export function getShippingSetting(
	shippingSettings: API.ShippingSetting[],
	country: CountryCode
): API.ShippingSetting | null {
	const setting =
		shippingSettings.find(s => s.countries.includes(country || '')) ??
		shippingSettings.find(s => s.conditions.some(m => m.rate.currencyCode === window.Shopify.currency.active)) ??
		shippingSettings.find(s => s.countries.includes('restOfWorld'))
	return setting ?? shippingSettings[0] ?? null
}
